import React from 'react';
import { useQuery } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import claimService from '@/services/claimService';
import { ClaimStatus } from '@/types';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { CLAIM_TYPE_LABELS } from '@/utils/constants';

interface ClaimDetailContentProps {
  id: string;
}

const getStatusStyles = (status: ClaimStatus) => {
  switch (status) {
    case ClaimStatus.PAID:
    case ClaimStatus.APPROVED:
      return { bg: 'bg-green-500', text: 'text-white', label: 'Approuvé' };
    case ClaimStatus.REJECTED:
      return { bg: 'bg-red-500', text: 'text-white', label: 'Rejeté' };
    default:
      return { bg: 'bg-orange-500', text: 'text-white', label: 'En cours' };
  }
};

const ClaimDetailContent: React.FC<ClaimDetailContentProps> = ({ id }) => {
  const { data: claim, isLoading, error } = useQuery({
    queryKey: ['claim', id],
    queryFn: () => claimService.getClaimDetail(id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
        <MaterialIcon icon="error_outline" className="text-4xl text-gray-400" />
        <p className="text-gray-500">Impossible de charger la réclamation</p>
      </div>
    );
  }

  const statusStyle = getStatusStyles(claim.status);
  const awaitingTransmission =
    claim.agTransmitted === false &&
    [ClaimStatus.SUBMITTED, ClaimStatus.PENDING].includes(claim.status);

  return (
    <div className="flex flex-col gap-6">
      {awaitingTransmission && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-4"
        >
          <MaterialIcon icon="sync_problem" className="text-amber-600 dark:text-amber-400 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-900 dark:text-amber-100">Transmission au gestionnaire en attente</p>
            <p className="text-xs mt-1 leading-snug text-amber-800 dark:text-amber-200/90">
              Votre demande est enregistrée mais n'a pas encore été reçue par votre gestionnaire.
              Elle ne sera traitée qu'après transmission. Si le problème persiste, contactez le support.
            </p>
          </div>
        </div>
      )}
      <section className="flex flex-col gap-4 rounded-2xl bg-surface-light dark:bg-surface-dark p-6 shadow-sm border border-gray-100 dark:border-white/5">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Numéro de dossier</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{claim.claimNumber}</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${statusStyle.bg} ${statusStyle.text}`}>
            {statusStyle.label}
          </div>
        </div>
        <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-white/5">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Date de soin</p>
            <p className="font-medium text-gray-900 dark:text-white">{formatDate(claim.treatmentDate)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-right">Montant réclamé</p>
            <p className="font-bold text-gray-900 dark:text-white text-right">{formatCurrency(claim.amount)}</p>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Prestataire</h2>
        <div className="rounded-xl bg-white dark:bg-surface-dark p-4 border border-gray-100 dark:border-white/5 flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MaterialIcon icon="local_hospital" />
          </div>
          <div>
            <p className="font-bold text-gray-900 dark:text-white">{claim.providerName}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{CLAIM_TYPE_LABELS[claim.type] ?? claim.type}</p>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Détail financier</h2>
        <div className="rounded-xl bg-white dark:bg-surface-dark p-4 border border-gray-100 dark:border-white/5 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Montant total</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(claim.amount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Base de remboursement</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(claim.approvedAmount)}</span>
          </div>
          <div className="h-px bg-gray-100 dark:bg-gray-800" />
          <div className="flex justify-between items-center text-primary-dark dark:text-primary">
            <span className="font-bold">Remboursé</span>
            <span className="text-xl font-bold">{formatCurrency(claim.reimbursedAmount)}</span>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Justificatifs</h2>
        {claim.documents && claim.documents.length > 0 ? (
          <ul className="grid gap-3">
            {claim.documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center justify-between rounded-xl bg-white dark:bg-surface-dark p-4 border border-gray-100 dark:border-white/5"
              >
                <div className="flex items-center gap-3">
                  <MaterialIcon icon="description" className="text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm line-clamp-1">{doc.fileName}</p>
                    <p className="text-xs text-gray-500">
                      {(doc.fileSize / 1024).toFixed(0)} KB • {new Date(doc.uploadDate || '').toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <a
                  href={doc.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Ouvrir ${doc.fileName}`}
                  className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                >
                  <MaterialIcon icon="visibility" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 italic">Aucun document joint</p>
        )}
      </section>
    </div>
  );
};

export default ClaimDetailContent;
